const traccarHelper = require('../util/traccar')
const { getDriverData } = require('../util/driver')
const { getCanDriverStyleMessages, parseCanDriverStyleMessage } = require('../util/xpert')
const { calculateRPMSections } = require('./performance-report')
const { getTranslations, isClientSide, convertToLocaleString, convertMS } = require('../util/utils')
const jsPDF = require('jspdf')
const { headerFromUser, addTable } = require('../util/pdfDocument')
const { getStyle } = require('../reportStyle')
const { getUserPartner } = require('fleetmap-partners')
const { getOverspeedEvents } = require('../speeding-report')
const { calculateConsumption } = require('../util/fuel')
const { getCanAvgConsumption } = require('../fuel-consumption-report')
const automaticReports = require('../automaticReports')

const distanceCoefficient = 0.5
const consumptionCoefficient = 0.7
const rpmCoefficient = 0.8
const hardBrakingCoefficient = 0.10
const hardAccelerationCoefficient = 0.10
const hardCorneringCoefficient = 0.10
const overspeedCoefficient = 0.15
const geofenceCoefficient = 0.15
const continuesDrivingCoefficient = 0.1
const reverseCoefficient = 0.1
const otherCoefficient = 0.1

async function create (from, to, userData, traccar) {
  const reportData = {
    drivers: []
  }

  const devices = userData.devices
  userData.geofences = []
  userData.roadSpeedLimits = true

  let deviceCount = 0
  const driversData = new Map()
  const overspeedEvents = await getOverspeedEvents(from, to, userData, devices, userData, traccar)

  const sliced = automaticReports.sliceArray(devices, 5)

  for (const slice of sliced) {
    const allInOne = await traccarHelper.getAllInOne(traccar, from, to, slice, true, true, false, false, deviceCount, devices.length)

    const result = []
    const requests = []
    for (const _chunk of automaticReports.sliceArray(slice.map(d => d.id), 1)) {
      requests.push(traccar.reports.reportsEventsGet(from, to, _chunk, null, ['alarm']))
    }

    try {
      result.push(...(await Promise.all(requests)))
    } catch (e) {
      console.log(e)
    }
    const alarmEvents = result.flatMap(d => d.data)

    for (const device of slice) {
      const route = allInOne.route.filter(t => t.deviceId === device.id)
      const trips = allInOne.trips.filter(t => t.deviceId === device.id)

      const deviceOverspeedEvents = overspeedEvents.find(e => e.device.id === device.id)
      const deviceAlarmEvents = alarmEvents.filter(e => e.deviceId === device.id)

      await processDevice(driversData, device, userData, { route, trips }, deviceAlarmEvents, deviceOverspeedEvents)
      deviceCount++
    }
  }

  const allDriversData = (Array.from(driversData.values())).filter(d => d.distance > 0)
  allDriversData.forEach(d => {
    const distanceScore = (Math.abs(d.distance - userData.distanceTarget) / userData.distanceTarget) * distanceCoefficient
    const rpmScore = ((d.highEngineRPM / 60000) / userData.rpmTarget) * rpmCoefficient
    const hardBrakingScore = (d.hardBraking / userData.hardBrakingTarget) * hardBrakingCoefficient
    const hardAccelerationScore = (d.hardAcceleration / userData.hardAccelerationTarget) * hardAccelerationCoefficient
    const hardCorneringScore = (d.hardCornering / userData.hardCorneringTarget) * hardCorneringCoefficient
    const overspeedScore = (d.overspeed / userData.overspeedTarget) * overspeedCoefficient
    const consumptionScore = (getCanAvgConsumption(d.distance, d.spentFuel).byKms / userData.fuelConsumptionTarget) * consumptionCoefficient
    const geofenceAlarmScore = (d.geofenceAlarm / userData.geofenceTarget) * geofenceCoefficient
    const continuesDrivingAlarmScore = (d.continuesDrivingAlarm / userData.continuesDrivingTarget) * continuesDrivingCoefficient
    const reverseAlarmScore = (d.reverseAlarm / userData.reverseTarget) * reverseCoefficient
    const otherAlarmScore = (d.otherAlarm / userData.otherTarget) * otherCoefficient

    d.avgFuelConsumption = getCanAvgConsumption(d.distance, d.spentFuel).byKms
    d.score = distanceScore + rpmScore + hardBrakingScore + hardAccelerationScore +
        hardCorneringScore + consumptionScore + overspeedScore + geofenceAlarmScore +
        continuesDrivingAlarmScore + reverseAlarmScore + otherAlarmScore
  })
  allDriversData.sort((a, b) => (a.score > b.score) ? 1 : -1)

  reportData.drivers.push(...allDriversData)

  reportData.target = {
    distanceTarget: userData.distanceTarget,
    rpmTarget: userData.rpmTarget,
    hardBrakingTarget: userData.hardBrakingTarget,
    hardAccelerationTarget: userData.hardAccelerationTarget,
    hardCorneringTarget: userData.hardCorneringTarget,
    fuelConsumptionTarget: userData.fuelConsumptionTarget,
    overspeedTarget: userData.overspeedTarget,
    geofenceTarget: userData.geofenceTarget,
    continuesDrivingTarget: userData.continuesDrivingTarget,
    reverseTarget: userData.reverseTarget,
    otherTarget: userData.otherTarget
  }

  reportData.coefficient = {
    distanceCoefficient,
    rpmCoefficient,
    hardBrakingCoefficient,
    hardAccelerationCoefficient,
    hardCorneringCoefficient,
    consumptionCoefficient,
    overspeedCoefficient,
    geofenceCoefficient,
    continuesDrivingCoefficient,
    reverseCoefficient,
    otherCoefficient
  }

  return reportData
}

async function processDevice (driversData, device, userData, allInOne, alarmEvents, overspeedEvents) {
  for (const d of userData.drivers) {
    const { trips, route } = await getDriverData(d, allInOne, userData)
    const positionsIds = route.map(p => p.id)
    const events = alarmEvents.filter(e => positionsIds.includes(e.positionId))
    const driverOverSpeedEvents = overspeedEvents ? overspeedEvents.alerts.filter(e => positionsIds.includes(e.position.id)) : []
    const spentFuel = calculateConsumption(device, { trips, route })

    const geofenceAlarm = events.filter(e => e.attributes.alarm === 'la ceinture').length
    const continuesDrivingAlarm = events.filter(e => e.attributes.alarm === 'Conduite continue').length
    const reverseAlarm = events.filter(e => e.attributes.alarm === 'MARCHE ARIERRE').length
    const otherAlarm = events.length - (geofenceAlarm + continuesDrivingAlarm + reverseAlarm)

    let driverData = driversData.get(d.id)
    if (!driverData) {
      driverData = {
        name: d.name,
        distance: 0,
        highEngineRPM: 0,
        hardBraking: 0,
        hardAcceleration: 0,
        hardCornering: 0,
        overspeed: 0,
        spentFuel: 0,
        geofenceAlarm: 0,
        continuesDrivingAlarm: 0,
        reverseAlarm: 0,
        otherAlarm: 0
      }
      driversData.set(d.id, driverData)
    }

    let highEngineRPM = 0
    let hardBraking = 0
    let hardAcceleration = 0
    let hardCornering = 0
    if (route.find(p => p.attributes.rawXpert)) {
      const xpertMessages = route.map(p => p.attributes.rawXpert).filter(a => a)
      const canDriverStyle = getCanDriverStyleMessages(xpertMessages).map(m => parseCanDriverStyleMessage(m))
      highEngineRPM = canDriverStyle.reduce((a, b) => a + b.totalTimeWithHighRpmAndTorque, 0)
      hardBraking = canDriverStyle.reduce((a, b) => a + b.totalNumberOfHarshBrakes, 0)
      hardAcceleration = canDriverStyle.reduce((a, b) => a + b.totalNumberOfHarshAccelerations, 0)
    } else {
      const highEngineRPMSections = calculateRPMSections(route, d.attributes.highRPM || 1500)
      highEngineRPM = highEngineRPMSections.map(a => a.length > 1
        ? a.map((p, index) => index === 0 ? 0 : new Date(p.fixTime).getTime() - new Date(a[index - 1].fixTime).getTime()).reduce((a, b) => a + b, 0)
        : 0).reduce((a, b) => a + b, 0)

      hardBraking = route.filter(p => p.attributes.alarm === 'hardBraking').length
      hardAcceleration = route.filter(p => p.attributes.alarm === 'hardAcceleration').length
      hardCornering = route.filter(p => p.attributes.alarm === 'hardCornering').length
    }

    driverData.distance = driverData.distance + trips.filter(t => t.distance > 0).reduce((a, b) => a + b.distance, 0)
    driverData.highEngineRPM = driverData.highEngineRPM + highEngineRPM
    driverData.hardBraking = driverData.hardBraking + hardBraking
    driverData.hardAcceleration = driverData.hardAcceleration + hardAcceleration
    driverData.hardCornering = driverData.hardCornering + hardCornering
    driverData.overspeed = driverData.overspeed + driverOverSpeedEvents.length
    driverData.spentFuel = driverData.spentFuel + spentFuel
    driverData.geofenceAlarm = driverData.geofenceAlarm + geofenceAlarm
    driverData.continuesDrivingAlarm = driverData.continuesDrivingAlarm + continuesDrivingAlarm
    driverData.reverseAlarm = driverData.reverseAlarm + reverseAlarm
    driverData.otherAlarm = driverData.otherAlarm + otherAlarm
  }
}

const fileName = 'DriverRankingReport'
async function exportToExcel (userData, reportData) {
  console.log('Export to Excel')

  const translations = getTranslations(userData)

  const reportDataRows = reportData.drivers

  const settings = {
    sheetName: translations.report.titleDriverRankingReport, // The name of the sheet
    fileName // The name of the spreadsheet
  }

  const headers = [
    { label: translations.report.driver, value: 'name' },
    { label: translations.report.distance, value: 'distance' },
    { label: translations.report.avgFuelConsumption, value: 'avgFuelConsumption' },
    { label: translations.report.highEngineRPM, value: 'highEngineRPM' },
    { label: translations.report.hardBraking, value: 'hardBraking' },
    { label: translations.report.hardAcceleration, value: 'hardAcceleration' },
    { label: translations.report.hardCornering, value: 'hardCornering' },
    { label: translations.report.overspeedAlarm, value: 'overspeed' },
    { label: translations.report.geofenceAlarm, value: 'geofenceAlarm' },
    { label: translations.report.continuesDrivingAlarm, value: 'continuesDrivingAlarm' },
    { label: translations.report.reverseAlarm, value: 'reverseAlarm' },
    { label: translations.report.otherAlarm, value: 'otherAlarm' },
    { label: translations.report.score, value: 'score' }
  ]

  let data = []
  if (reportDataRows) {
    reportDataRows.forEach(row => {
      data = data.concat({
        name: row.name,
        distance: Number(row.distance.toFixed(1)),
        avgFuelConsumption: Number(row.avgFuelConsumption.toFixed(2)),
        highEngineRPM: convertMS(row.highEngineRPM, false),
        hardBraking: row.hardBraking,
        hardAcceleration: row.hardAcceleration,
        hardCornering: row.hardCornering,
        overspeed: row.overspeed,
        geofenceAlarm: row.geofenceAlarm,
        continuesDrivingAlarm: row.continuesDrivingAlarm,
        reverseAlarm: row.reverseAlarm,
        otherAlarm: row.otherAlarm,
        score: Number(row.score.toFixed(2))
      })
    })
    console.log(data)
    return {
      headers,
      data,
      settings
    }
  }
}

async function exportToPDF (userData, reportData) {
  console.log('exportDelayedStartReportToPDF')

  const timezone = userData.user.attributes.timezone
  const translations = getTranslations(userData)
  const lang = userData.user.attributes.lang || (isClientSide() && navigator.language)
  const reportDataRows = reportData.drivers

  const headers = [
    translations.report.driver,
    translations.report.distance,
    translations.report.avgFuelConsumption,
    translations.report.highEngineRPM,
    translations.report.hardBraking,
    translations.report.hardAcceleration,
    translations.report.hardCornering,
    translations.report.overspeedAlarm,
    translations.report.geofenceAlarm,
    translations.report.continuesDrivingAlarm,
    translations.report.reverseAlarm,
    translations.report.otherAlarm,
    translations.report.score
  ]

  if (reportDataRows) {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF.jsPDF('l')
    await headerFromUser(doc, translations.report.titleDriverRankingReport, userData.user)

    doc.setFontSize(13)
    doc.text(convertToLocaleString(reportData.from, lang, timezone, userData.user) + ' - ' + convertToLocaleString(reportData.to, lang, timezone, userData.user), 20, 25)

    const data = []
    reportDataRows.forEach(function (row) {
      const temp = [
        row.name,
        row.distance.toFixed(1),
        row.avgFuelConsumption.toFixed(2),
        convertMS(row.highEngineRPM, false),
        row.hardBraking,
        row.hardAcceleration,
        row.hardCornering,
        row.overspeed,
        row.geofenceAlarm,
        row.continuesDrivingAlarm,
        row.reverseAlarm,
        row.otherAlarm,
        row.score.toFixed(2)
      ]
      data.push(temp)
    })
    const footValues = []

    const style = getStyle(getUserPartner(userData.user))

    addTable(doc, headers, data, footValues, style, 40, {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right' },
      10: { halign: 'right' },
      11: { halign: 'right' },
      12: { halign: 'right' }
    })
    return doc
  }
}

exports.create = create
exports.exportToPDF = exportToPDF
exports.exportToExcel = exportToExcel
